import reverse from 'lodash/reverse';
import sortBy from 'lodash/sortBy';
import { storableError } from '../../util/errors';
import { parse } from '../../util/urlHelpers';
import { getAllTransitionsForEveryProcess } from '../../transactions/transaction';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { fetchShopifyFeeEvents } from '../../ducks/ShopifyOnboardingPage.duck';
import { transitions } from '../../transactions/transactionProcessPurchase';
import { inquiryTab, OffersTab, shopifyCheckoutOrdersTab } from '../../util/types';
import { fetchTransactionsWithMetadata } from '../../util/api';

const sortedTransactions = txs =>
  reverse(
    sortBy(txs, tx => {
      return tx.attributes ? tx.attributes.lastTransitionedAt : null;
    })
  );

// ================ Action types ================ //

export const FETCH_ORDERS_OR_SALES_REQUEST = 'app/InboxPage/FETCH_ORDERS_OR_SALES_REQUEST';
export const FETCH_ORDERS_OR_SALES_SUCCESS = 'app/InboxPage/FETCH_ORDERS_OR_SALES_SUCCESS';
export const FETCH_ORDERS_OR_SALES_ERROR = 'app/InboxPage/FETCH_ORDERS_OR_SALES_ERROR';

export const FETCH_INQUIRY_UNREAD_COUNT_REQUEST = 'app/InboxPage/FETCH_INQUIRY_UNREAD_COUNT_REQUEST';
export const FETCH_INQUIRY_UNREAD_COUNT_SUCCESS = 'app/InboxPage/FETCH_INQUIRY_UNREAD_COUNT_SUCCESS';
export const FETCH_INQUIRY_UNREAD_COUNT_ERROR = 'app/InboxPage/FETCH_INQUIRY_UNREAD_COUNT_ERROR';

// ================ Reducer ================ //

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

const initialState = {
  fetchInProgress: false,
  fetchOrdersOrSalesError: null,
  pagination: null,
  transactionRefs: [],
  inquiryUnreadCount: 0,
  fetchInquiryUnreadCountInProgress: false,
  fetchInquiryUnreadCountError: null,
  transactionsMetadata: {}, // Map of transaction ID to lastMessageDetails
};

export default function inboxPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_ORDERS_OR_SALES_REQUEST:
      return { ...state, fetchInProgress: true, fetchOrdersOrSalesError: null };
    case FETCH_ORDERS_OR_SALES_SUCCESS: {
      const transactions = sortedTransactions(payload.data.data);
      return {
        ...state,
        fetchInProgress: false,
        transactionRefs: entityRefs(transactions),
        pagination: payload.data.meta,
      };
    }
    case FETCH_ORDERS_OR_SALES_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, fetchInProgress: false, fetchOrdersOrSalesError: payload };

    case FETCH_INQUIRY_UNREAD_COUNT_REQUEST:
      return { ...state, fetchInquiryUnreadCountInProgress: true, fetchInquiryUnreadCountError: null };
    case FETCH_INQUIRY_UNREAD_COUNT_SUCCESS:
      return { 
        ...state, 
        fetchInquiryUnreadCountInProgress: false, 
        inquiryUnreadCount: payload.count,
        transactionsMetadata: payload.metadata,
      };
    case FETCH_INQUIRY_UNREAD_COUNT_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, fetchInquiryUnreadCountInProgress: false, fetchInquiryUnreadCountError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

const fetchOrdersOrSalesRequest = () => ({ type: FETCH_ORDERS_OR_SALES_REQUEST });
const fetchOrdersOrSalesSuccess = response => ({
  type: FETCH_ORDERS_OR_SALES_SUCCESS,
  payload: response,
});
const fetchOrdersOrSalesError = e => ({
  type: FETCH_ORDERS_OR_SALES_ERROR,
  error: true,
  payload: e,
});

const fetchInquiryUnreadCountRequest = () => ({ type: FETCH_INQUIRY_UNREAD_COUNT_REQUEST });
const fetchInquiryUnreadCountSuccess = count => ({
  type: FETCH_INQUIRY_UNREAD_COUNT_SUCCESS,
  payload: count,
});
const fetchInquiryUnreadCountError = e => ({
  type: FETCH_INQUIRY_UNREAD_COUNT_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

const INBOX_PAGE_SIZE = 10;

export const loadData = (params, search) => (dispatch, getState, sdk) => {
  const { tab } = params;

  const onlyFilterValues = {
    orders: 'order',
    sales: 'sale',
    offers: 'sale'   // please change it according to transaction
  };

  const onlyFilter = onlyFilterValues[tab];
  if (!onlyFilter && tab != inquiryTab && tab !== shopifyCheckoutOrdersTab) {
    return Promise.reject(new Error(`Invalid tab for InboxPage: ${tab}`));
  }

  if (tab === shopifyCheckoutOrdersTab) {
    dispatch(fetchOrdersOrSalesSuccess({ data: { data: [], meta: null } }));
    return dispatch(fetchCurrentUser()).then(() => {
      const state = getState();
      const currentUserId = state.user.currentUser?.id?.uuid;

      if (!currentUserId) {
        return null;
      }

      return Promise.all([
        dispatch(
          fetchShopifyFeeEvents({
            userId: currentUserId,
            checkoutStatus: 'COMPLETED',
            reportRole: 'buyer',
          })
        ),
        dispatch(fetchInquiryUnreadCount(currentUserId)),
      ]);
    });
  }

  dispatch(fetchOrdersOrSalesRequest());

  const { page = 1 } = parse(search);

  const apiQueryParams = {
    include: [
      'listing',
      'provider',
      'provider.profileImage',
      'customer',
      'customer.profileImage',
      'booking',
    ],
    'fields.transaction': [
      'processName',
      'lastTransition',
      'lastTransitionedAt',
      'createdAt',
      'transitions',
      'payinTotal',
      'payoutTotal',
      'lineItems',
      'protectedData'
    ],
    'fields.listing': ['title', 'availabilityPlan', 'publicData.listingType'],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'deleted', 'banned'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
    page,
    perPage: INBOX_PAGE_SIZE,
  };


  const offerTransactions = [
    "transition/confirm-offer-payment",
    "transition/cancel-offer-customer",
    "transition/cancel-offer-provider",
    // "transition/accept-offer-provider",
    "transition/expire-offer-payment", 
  ];
  const freeMessage =[
    "transition/inquire-without-payment", 
  ]

  // INQUIREY TAB
  if(tab == inquiryTab){
    Object.assign(apiQueryParams,{
      lastTransitions: [transitions.INQUIRE, "transition/inquire-without-payment"],
    })
  }
  // Offer transactions
  else if(tab == OffersTab){
    Object.assign(apiQueryParams,{
      lastTransitions: offerTransactions,
    })
  }
  // sale and orders
  else {
     Object.assign(apiQueryParams,{
      only: onlyFilter,
      lastTransitions: getAllTransitionsForEveryProcess().filter(
        st => !offerTransactions.includes(st) && !freeMessage.includes(st)
      ),
     })
  }

  return sdk.transactions
    .query(apiQueryParams)
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(fetchOrdersOrSalesSuccess(response));
      
      // Always fetch inquiry unread count when inbox page loads (regardless of tab)
      const state = getState();
      const currentUser = state.user.currentUser;
      const currentUserId = currentUser?.id?.uuid;
      
      if (currentUserId) {
        dispatch(fetchInquiryUnreadCount(currentUserId));
      }
      
      return response;
    })
    .catch(e => {
      dispatch(fetchOrdersOrSalesError(storableError(e)));
      throw e;
    });
};

// Fetch inquiry unread count from backend
export const fetchInquiryUnreadCount = (userId) => (dispatch) => {
  dispatch(fetchInquiryUnreadCountRequest());

  return fetchTransactionsWithMetadata(userId, 'all')
    .then(response => {
      let totalUnreadCount = 0;
      const metadataMap = {}; // Map of transaction ID to lastMessageDetails

      if (response.success) {
        const transactionsMetadata = response.transactionsMetadata || response.transactions || [];

        transactionsMetadata.forEach(tx => {
          metadataMap[tx.id] = tx.lastMessageDetails;

          const details = tx.lastMessageDetails || {};
          if (
            details.lastMessageSenderId &&
            details.lastMessageSenderId !== userId &&
            !details.lastMessageReadByOtherParty
          ) {
            totalUnreadCount++;
          }
        });
      }

      dispatch(fetchInquiryUnreadCountSuccess({ count: totalUnreadCount, metadata: metadataMap }));
      return totalUnreadCount;
    })
    .catch(error => {
      console.error('Failed to fetch inquiry unread count:', error);
      dispatch(fetchInquiryUnreadCountError(storableError(error)));
    });
};
